import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, HashNavigation, Keyboard, Navigation } from 'swiper';

import { Text } from '@/components/typography';
import ButtonArrow from '@/components/button/ButtonArrow/ButtonArrow';
import ButtonClose from '@/components/button/ButtonClose/ButtonClose';
import { Element, FigCaption, Figure, VideoWrapper } from './MediaSlidesGallery.styles';
import MediaSlidesGalleryContext from './context/MediaSlidesGalleryContext';
import MediaImage from './MediaImage';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import { useTranslations } from 'next-intl';

export interface MediaSlidesGalleryProps {
  images: MediaObjType[];
  /**The id prop represents the aria-labelledby attribute for the modal, and is required for a11y*/
  id?: string;
  /**Show or hide the modal.*/
  open: boolean;
  /**Function that toggles the modal.*/
  toggle: () => void;
}

const MediaSlidesGallery = ({ id, images, open, toggle }: MediaSlidesGalleryProps) => {
  const t = useTranslations('Media');
  const ref = useRef<HTMLDivElement | null>(null);
  const context = useMemo(() => ({ open, toggle, images }), [images, open, toggle]);
  const [show, setShow] = useState(false);

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || ref.current === null) return;
    toggle();
  };

  useEffect(() => {
    const body = document.querySelector('body');
    if (open && ref.current) {
      if (body) {
        body.style.overflow = 'hidden';
      }
      setTimeout(() => {
        ref.current?.setAttribute('aria-hidden', `${!open}`);
      }, 15);
    } else {
      if (body) {
        body.style.overflow = '';
      }
    }
  }, [open]);

  useEffect(() => {
    document.body.addEventListener('keyup', handleEscape, false);

    // unmount
    return () => {
      document.body.removeEventListener('keyup', handleEscape, false);
      const body = document.querySelector('body');
      if (body) {
        body.style.overflow = '';
      }
    };
  });

  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 500);
  }, []);

  if (!images.length) return null;

  return (
    <MediaSlidesGalleryContext.Provider value={context}>
      {createPortal(
        <Element ref={ref} aria-hidden={true} id={id}>
          <Swiper
            navigation={{
              enabled: true, //!mobile,
              nextEl: `.media-gallery-next`,
              prevEl: `.media-gallery-prev`,
            }}
            keyboard={{
              enabled: true,
            }}
            hashNavigation={{
              watchState: true,
              replaceState: false,
            }}
            modules={[A11y, Keyboard, Navigation, HashNavigation]}
            slidesPerView={1}
          >
            {images.map((image, index) => (
              <SwiperSlide
                key={`${image.filename}-${index}`}
                data-hash={image.filename?.replace(/\.jpg/, '')}
                onClick={toggle}
              >
                <Figure show={show}>
                  {(image.resource_type === 'video' && (
                    <VideoWrapper>
                      <iframe src={`https://www.youtube.com/embed/${image.filename}?autoplay=1`} />
                    </VideoWrapper>
                  )) || <MediaImage media={image} />}

                  <FigCaption>
                    <Text>{image.description}</Text>
                    <Text size="xs">
                      &copy; {(image.credits && `${image.credits}`) || ``} - {t('Imagens meramente ilustrativas')}
                    </Text>
                  </FigCaption>
                </Figure>
              </SwiperSlide>
            ))}
          </Swiper>
          <ButtonArrow mobile={false} appearence="circle" direction="left" className="media-gallery-prev" />
          <ButtonArrow mobile={false} appearence="circle" direction="right" className="media-gallery-next" />
          <ButtonClose size={32} color={'neutral_400'} onClick={toggle} />
        </Element>,
        document.body
      )}
    </MediaSlidesGalleryContext.Provider>
  );
};

export default MediaSlidesGallery;
